import { computed, onMounted, ref, watch, type Ref } from "vue";
import { storeToRefs } from "pinia";
import useBasicStore from "~/store/modules/basic";

type OcrFileItem = {
  pageNumber?: number;
  type?: number;
};

type UseOcrCostCharParams = {
  files: Ref<OcrFileItem[]>;
  openImage: Ref<boolean | undefined>;
};

const OCR_SUPPORTED_TYPES = [1, 3, 4, 5];

export const useOcrCostChar = ({ files, openImage }: UseOcrCostCharParams) => {
  const basicStore = useBasicStore();
  const { homePageConfig } = storeToRefs(basicStore);
  const showDialog = ref(false);

  const canUseOcr = computed(() => {
    return files.value.length > 0 && files.value.every((file) => OCR_SUPPORTED_TYPES.includes(Number(file?.type)));
  });

  const isAllPdf = computed(() => {
    return files.value.length > 0 && files.value.every((file) => Number(file?.type) === 1);
  });

  const totalPages = computed(() => {
    if (!isAllPdf.value) {
      return 0;
    }

    return files.value.reduce((sum: number, file: OcrFileItem) => {
      return sum + (Number(file?.pageNumber) || 0);
    }, 0);
  });

  const ocrCostWord = computed(() => Number(homePageConfig.value?.bizOcrCostWord || 0));
  const totalCost = computed(() => ocrCostWord.value * totalPages.value);
  const showPdfStats = computed(() => {
    return !!openImage.value && isAllPdf.value && totalPages.value > 0;
  });

  const formatNumber = (value: number) => Number(value || 0).toLocaleString();

  const ensureDefaultValue = () => {
    if (!canUseOcr.value) {
      return;
    }

    // 支持 OCR 的文件统一默认勾选图片翻译，不再按国内外区分
    if (openImage.value === undefined || openImage.value === null) {
      openImage.value = true;
    }
  };

  const closeDialog = () => {
    showDialog.value = false;
  };

  const confirmEnable = () => {
    openImage.value = true;
    closeDialog();
  };

  const handleToggleClick = () => {
    if (openImage.value) {
      openImage.value = false;
      return;
    }

    // 暂不保留「国内点击弹消耗确认窗」逻辑：统一直接勾选、不弹窗
    // showDialog / confirmEnable 保留为休眠态，环境就绪后恢复此分支即可重新启用国内弹窗
    openImage.value = true;
  };

  onMounted(() => {
    if (homePageConfig.value?.ocrCostWord === undefined || homePageConfig.value?.ocrCostWord === null) {
      basicStore.getHomePageConfig();
    }
  });

  watch([canUseOcr, openImage], () => {
    ensureDefaultValue();
  }, { immediate: true });

  return {
    closeDialog,
    confirmEnable,
    formatNumber,
    handleToggleClick,
    isAllPdf,
    ocrCostWord,
    showDialog,
    showPdfStats,
    totalCost,
    totalPages,
  };
};
